import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable, catchError, throwError } from 'rxjs';
import { isSafeRedirectUrl } from '@core/routing/safe-redirect-url';
import { AuthService } from '@features/auth/data-access/auth.service';

@Injectable({ providedIn: 'root' })
export class AuthExpiredInterceptor {
  private readonly injector = inject(Injector);
  private expireInFlight = false;

  public intercept(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return next(req).pipe(
      catchError((error: unknown) => {
        if (this.shouldExpireSession(req, error)) {
          this.expireSession();
        }
        return throwError(() => error);
      }),
    );
  }

  private shouldExpireSession(req: HttpRequest<unknown>, error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }
    if (this.isIgnoredAuthRequest(req)) {
      return false;
    }
    return this.injector.get(AuthService).isAuthenticated();
  }

  private isIgnoredAuthRequest(req: HttpRequest<unknown>): boolean {
    const path = req.url.split('?')[0];
    return (
      path.endsWith('/auth/login') || path.endsWith('/auth/logout') || path.endsWith('/auth/me')
    );
  }

  private expireSession(): void {
    if (this.expireInFlight) {
      return;
    }
    this.expireInFlight = true;

    const authService = this.injector.get(AuthService);
    const router = this.injector.get(Router);
    const messageService = this.injector.get(MessageService);
    const translate = this.injector.get(TranslateService);
    const returnUrl = isSafeRedirectUrl(router.url) ? router.url : undefined;

    authService.logout();
    messageService.add({
      severity: 'warn',
      summary: translate.instant('auth.sessionExpired'),
    });
    void router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
  }
}

export const authExpiredInterceptor: HttpInterceptorFn = (req, next) =>
  inject(AuthExpiredInterceptor).intercept(req, next);
